/**
 * Shared base for the two market rooms — the Tausch trade board (ShopScene)
 * and the Weltkugel level gallery (GalleryScene). Owns the avatar, the
 * vertical platform stack, animated scroll, the right-edge scroll indicator,
 * the left/right doors, the top tab strip, and the select sword-swing.
 *
 * Subclasses supply their data list, how to render one card per row, what the
 * select button does, their door config, and the active tab. Tuned geometry
 * (STACK_TOP_Y / ROW_HEIGHT etc.) is shared via scenes/board/geometry so the
 * ~57 px single-jump arc clears exactly one row in both rooms.
 */
import Phaser from "phaser";

import { GAMEPLAY, NATIVE_HEIGHT, NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";
import type { StringKey } from "../i18n";
import { InputService } from "../services/InputService";
import { KNIGHT_ATTACK_ANIM, KNIGHT_IDLE_ANIM, KNIGHT_IDLE_KEY, KNIGHT_RUN_ANIM } from "./BootScene";
import {
  CARD_STACK_WIDTH,
  CARD_STACK_X,
  PLATFORM_HEIGHT,
  clampScrollOffset,
  platformTopY,
  rowTopY,
  selectedRow,
} from "./board/geometry";
import { atLeftDoor, atRightDoor, spawnForEntry, tabIntent } from "./board/navigation";
import type { BoardTab, SpawnEntry } from "./board/navigation";
import { ScrollIndicator } from "../ui/ScrollIndicator";

export const FLOOR_Y = NATIVE_HEIGHT - 28;
const DOOR_X_LEFT = 12;
const DOOR_X_RIGHT = NATIVE_WIDTH - 12;
const DEPTH_CARDS = 1;
const DEPTH_PLATFORMS = 2;
const DEPTH_HERO = 10;
const DEPTH_HUD = 20;
/** Ignore door triggers briefly after entering so an arriving hero spawned
 *  near a door never instantly re-fires it. */
const ENTRY_DEBOUNCE_MS = 250;

/** Anything the base tweens / lifecycles during scroll + select. */
export interface BoardCard {
  container: Phaser.GameObjects.Container;
  setSelected(selected: boolean): void;
  destroy(): void;
  /** Optional arrival flourish (shop offer cards). */
  animateArrive?(): void;
  /** Optional per-frame tick (shop offer countdowns). */
  tick?(nowMs: number): void;
  /** Optional tween to a position (shop accept-fly-off). */
  tweenTo?(x: number, y: number, durationMs: number, onComplete?: () => void): void;
  /** Optional rejection flash (shop unaffordable trade). */
  flashRejected?(): void;
}

export type DoorAction = "exitMarket" | "toGallery" | "toTrades";

export interface DoorConfig {
  action: DoorAction;
  labelKey: StringKey;
  hintKey?: StringKey;
  /** "doorSprite" = tower.up portal (market exit); "glowStrip" = thin
   *  right-wall glow (Tausch→globe); "signpost" = floor signpost prop hinting
   *  at the adjacent room (Weltkugel→trades). */
  render: "doorSprite" | "glowStrip" | "signpost";
}

export interface DoorsConfig {
  left: DoorConfig;
  right?: DoorConfig;
}

export interface BoardSceneInit {
  returnTo: "runner" | "launcher";
  walletSource: "runner" | "launcher";
  entry?: SpawnEntry;
}

export abstract class PlatformBoardScene extends Phaser.Scene {
  protected returnTo: BoardSceneInit["returnTo"] = "launcher";
  protected walletSource: BoardSceneInit["walletSource"] = "launcher";
  protected entry: SpawnEntry = "center";

  protected input2!: InputService;
  protected hero!: Phaser.Physics.Arcade.Sprite;
  protected cards: BoardCard[] = [];
  protected platforms: Phaser.GameObjects.Rectangle[] = [];
  protected platformGroup!: Phaser.Physics.Arcade.StaticGroup;
  protected scrollOffset = 0;
  protected scrollIndicator!: ScrollIndicator;
  protected animatingScroll = false;
  /** Drop-through window: while now < this, the one-way collider lets the
   *  hero fall straight through the platform it is standing on. */
  protected dropThroughUntilMs = 0;

  private prevScrollDir = 0;
  /** Edge guard so a held Up/W key hops the hero up only once per press. */
  private prevUpHeld = false;
  private dropThroughArmedAtMs = -Infinity;
  protected enteredAtMs = 0;
  private isSwinging = false;
  private lastSwingAtMs = -Infinity;
  /** How the in-flight tab switch was triggered — for subclass telemetry. */
  protected switchVia: "walk" | "toggle" = "walk";
  private doors!: DoorsConfig;
  private leftDoorGlow?: Phaser.GameObjects.Rectangle;
  private rightDoorGlow?: Phaser.GameObjects.Rectangle;

  // ---- subclass hooks -----------------------------------------------------
  protected abstract activeTab: BoardTab;
  protected abstract visibleRows(): number;
  protected abstract itemCount(): number;
  protected abstract createCard(itemIndex: number, x: number, y: number): BoardCard;
  protected abstract configureDoors(): DoorsConfig;
  protected abstract onSelectConfirm(selectedBufferIndex: number): void;
  protected abstract exitMarket(): void;
  /** Start the other tab's scene, carrying round-trip params + spawn entry. */
  protected abstract switchTab(target: BoardTab, entry: SpawnEntry): void;

  /** Per-frame extra work (action prompt, wallet, location…). */
  protected updateExtras(_input: ReturnType<InputService["read"]>, _dtMs: number): void {}
  /** React to the standing-selection changing (highlight detail, reviews…). */
  protected onSelectionChanged(_selectedBufferIndex: number): void {}
  /** A scroll just committed (shop: audio + telemetry). */
  protected onScrolled(_direction: "up" | "down", _source: "arrow" | "jump" | "drop"): void {}
  /** The hero just jumped (shop: jump audio). */
  protected onJumped(): void {}
  /** Called before renderCards rebuilds the visible cards (shop: hide tooltip). */
  protected beforeRenderCards(): void {}

  // ---- lifecycle ----------------------------------------------------------

  protected initBoard(data: Partial<BoardSceneInit>): void {
    this.returnTo = data.returnTo ?? "launcher";
    this.walletSource = data.walletSource ?? "launcher";
    this.entry = data.entry ?? "center";
  }

  /** Subclasses call this from create() AFTER drawing their background and
   *  resolving their data list + services. */
  protected buildBoard(): void {
    this.enteredAtMs = this.time.now;
    this.input2 = new InputService(this);
    this.doors = this.configureDoors();
    this.buildCastleBackdrop();
    this.buildTabStrip();
    this.spawnHero();
    this.buildPlatformStack();
    this.scrollIndicator = new ScrollIndicator(this, this.visibleRows());
    this.buildDoors();
    this.renderCards();
  }

  private spawnHero(): void {
    const spawn = spawnForEntry(this.entry, NATIVE_WIDTH);
    this.hero = this.physics.add.sprite(spawn.x, FLOOR_Y - 8, KNIGHT_IDLE_KEY, 0);
    this.hero.setOrigin(0.5, 1.0);
    this.hero.setDisplaySize(40, 40);
    this.hero.setCollideWorldBounds(true);
    this.hero.setDepth(DEPTH_HERO);
    this.hero.setFlipX(spawn.flipX);
    const body = this.hero.body as Phaser.Physics.Arcade.Body;
    body.setSize(20, 36);
    body.setOffset(22, 14);
    this.hero.play(KNIGHT_IDLE_ANIM);

    // Floor collide-surface. The knight sprite has ~9px of legs below its
    // physics body (64px frame → 40px display, body offset 14 + height 36), so
    // resting the body on FLOOR_Y+1 left the DRAWN feet ~6px below the floor's
    // top edge / the brick backdrop seam. Raise the surface ~9px so the drawn
    // feet rest on FLOOR_Y, aligned with the floor band + door/prop bases.
    const floor = this.physics.add.staticImage(NATIVE_WIDTH / 2, FLOOR_Y - 5, KNIGHT_IDLE_KEY);
    floor.setVisible(false);
    floor.body.setSize(NATIVE_WIDTH * 2, 8);
    this.physics.add.collider(this.hero, floor);

    this.physics.world.setBounds(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT);
  }

  private buildPlatformStack(): void {
    this.platformGroup = this.physics.add.staticGroup();
    const platformCenterX = CARD_STACK_X + CARD_STACK_WIDTH / 2;
    for (let i = 0; i < this.visibleRows(); i++) {
      const plat = this.add
        .rectangle(platformCenterX, platformTopY(i), CARD_STACK_WIDTH, PLATFORM_HEIGHT, PALETTE.shopFloorEdge, 1)
        .setOrigin(0.5, 0)
        .setDepth(DEPTH_PLATFORMS);
      plat.setStrokeStyle(1, PALETTE.shopWallHi);
      this.physics.add.existing(plat, true);
      this.platformGroup.add(plat);
      this.platforms.push(plat);
    }

    // One-way collider: hero only lands when falling from above. The
    // drop-through window makes the process callback return false so the
    // hero falls clean through.
    this.physics.add.collider(
      this.hero,
      this.platformGroup,
      undefined,
      (heroObj, platObj) => {
        if (this.time.now < this.dropThroughUntilMs) return false;
        const hb = (heroObj as Phaser.Physics.Arcade.Sprite).body as Phaser.Physics.Arcade.Body | null;
        const ps = platObj as Phaser.GameObjects.Rectangle;
        if (!hb) return false;
        const platTop = ps.y;
        const heroBottom = hb.y + hb.height;
        return hb.velocity.y > 0 && heroBottom <= platTop + 4;
      },
      this,
    );
  }

  private buildTabStrip(): void {
    const cx = NATIVE_WIDTH / 2;
    const left = this.activeTab === "trades";
    const tradesTab = this.add
      .text(cx - 52, 12, t("gallery.tab.trades"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.hex(left ? UGC_CYAN : PALETTE.parchment),
        fontStyle: left ? "bold" : "normal",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD)
      .setInteractive({ useHandCursor: true });
    tradesTab.on("pointerdown", () => this.requestTab("trades"));
    this.add
      .text(cx + 2, 12, "|", { fontFamily: "monospace", fontSize: "9px", color: this.hex(PALETTE.hudDim) })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD);
    const globeTab = this.add
      .text(cx + 56, 12, t("gallery.tab.gallery"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.hex(left ? PALETTE.parchment : UGC_CYAN),
        fontStyle: left ? "normal" : "bold",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_HUD)
      .setInteractive({ useHandCursor: true });
    globeTab.on("pointerdown", () => this.requestTab("gallery"));
  }

  private requestTab(target: BoardTab): void {
    if (target === this.activeTab) return;
    this.switchVia = "toggle";
    // Arrive on the side adjacent to the room you came from.
    const entry: SpawnEntry = target === "gallery" ? "fromLeft" : "fromRight";
    this.switchTab(target, entry);
  }

  private buildDoors(): void {
    this.leftDoorGlow = this.buildDoor(this.doors.left, "left");
    if (this.doors.right) this.rightDoorGlow = this.buildDoor(this.doors.right, "right");
  }

  private buildDoor(cfg: DoorConfig, side: "left" | "right"): Phaser.GameObjects.Rectangle {
    if (cfg.render === "glowStrip") {
      // Thin right-wall glow + arrow label; coexists with the scroll bar
      // (x=NATIVE_WIDTH-16) and the bottom-right location compass.
      const glow = this.add
        .rectangle(NATIVE_WIDTH - 2, rowTopY(0), 4, FLOOR_Y - rowTopY(0), UGC_CYAN, 0.3)
        .setOrigin(0.5, 0)
        .setDepth(0);
      this.add
        .text(NATIVE_WIDTH - 6, rowTopY(0) - 13, t(cfg.labelKey), {
          fontFamily: "monospace",
          fontSize: "6px",
          color: this.hex(UGC_CYAN),
          fontStyle: "bold",
        })
        .setOrigin(1, 0)
        .setDepth(DEPTH_HUD);
      this.buildGlobeSignpost();
      return glow;
    }

    if (cfg.render === "signpost") {
      // Floor signpost instead of a portal — hints at the adjacent room
      // (Weltkugel → trades). The walk-into-the-edge trigger is unchanged.
      const sx = side === "left" ? 28 : NATIVE_WIDTH - 28;
      const arrow = side === "left" ? "◀" : "▶";
      const glow = this.add.rectangle(sx, FLOOR_Y - 30, 22, 34, UGC_CYAN, 0.22).setOrigin(0.5, 0).setDepth(0);
      this.add.rectangle(sx, FLOOR_Y, 3, 26, PALETTE.shopCardEdge, 1).setOrigin(0.5, 1).setDepth(1);
      const boardOriginX = side === "left" ? 1 : 0;
      const boardX = side === "left" ? sx - 1 : sx + 1;
      this.add
        .rectangle(boardX, FLOOR_Y - 18, 30, 13, PALETTE.parchment, 1)
        .setOrigin(boardOriginX, 0.5)
        .setStrokeStyle(1, PALETTE.shopCardEdge)
        .setDepth(1);
      this.add
        .text(side === "left" ? boardX - 15 : boardX + 15, FLOOR_Y - 18, `${arrow} $`, {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.hex(PALETTE.geo),
          fontStyle: "bold",
        })
        .setOrigin(0.5, 0.5)
        .setDepth(1);
      this.add
        .text(sx, FLOOR_Y - 34, t(cfg.labelKey), {
          fontFamily: "monospace",
          fontSize: "6px",
          color: this.hex(PALETTE.parchment),
        })
        .setOrigin(0.5, 0)
        .setDepth(DEPTH_HUD);
      return glow;
    }

    const x = side === "left" ? DOOR_X_LEFT : DOOR_X_RIGHT;
    const glow = this.add.rectangle(x, FLOOR_Y - 30, 22, 34, UGC_CYAN, 0.35).setOrigin(0.5, 0).setDepth(0);
    const img = this.add.image(x, FLOOR_Y, "tower.up").setOrigin(0.5, 1).setScale(0.5).setDepth(0);
    if (side === "right") img.setFlipX(true);
    this.add
      .text(x, FLOOR_Y - 34, t(cfg.labelKey), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.hex(PALETTE.parchment),
      })
      .setOrigin(0.5, 0)
      .setDepth(DEPTH_HUD);
    if (cfg.hintKey) {
      this.add
        .text(side === "left" ? x + 14 : x - 14, FLOOR_Y - 42, t(cfg.hintKey), {
          fontFamily: "monospace",
          fontSize: "6px",
          color: this.hex(UGC_CYAN),
        })
        .setOrigin(side === "left" ? 0 : 1, 0)
        .setDepth(DEPTH_HUD);
    }
    return glow;
  }

  // ---- castle backdrop ----------------------------------------------------

  /**
   * Castle-interior back wall shared by both rooms: a two-tone brick course
   * pattern, an arched night-sky window centered up high, and two flickering
   * wall torches in the side margins. All at depth 0 so the cards (depth
   * {@link DEPTH_CARDS}) and hero render in front. (Procedural placeholder —
   * swap for a tileset/parallax asset later; see notes.)
   */
  private buildCastleBackdrop(): void {
    const g = this.add.graphics().setDepth(0);
    const top = 24;
    const bh = 12;
    const bw = 26;
    g.fillStyle(PALETTE.shopWall, 1);
    g.fillRect(0, top, NATIVE_WIDTH, FLOOR_Y - top);
    let row = 0;
    for (let y = top; y < FLOOR_Y; y += bh) {
      const offset = (row % 2) * (bw / 2);
      for (let bx = -bw; bx < NATIVE_WIDTH; bx += bw) {
        g.fillStyle(PALETTE.shopWallHi, 1);
        g.fillRect(bx + offset + 1, y + 1, bw - 2, bh - 2);
      }
      row++;
    }

    // Arched night-sky window (centered, up high — top peeks above the stack).
    const wx = NATIVE_WIDTH / 2;
    const wTop = 27;
    const wW = 54;
    const wH = 30;
    this.add.rectangle(wx, wTop - 3, wW + 6, wH + 6, PALETTE.shopFloorEdge, 1).setOrigin(0.5, 0).setDepth(0);
    this.add.rectangle(wx, wTop, wW, wH, 0x16223a, 1).setOrigin(0.5, 0).setDepth(0);
    // arch cap
    this.add.triangle(wx, wTop, -wW / 2, 0, wW / 2, 0, 0, -6, PALETTE.shopFloorEdge, 1).setDepth(0);
    // a few stars
    const starXs = [-18, -6, 9, 16, -12, 4];
    const starYs = [8, 18, 6, 20, 24, 13];
    for (let i = 0; i < starXs.length; i++) {
      this.add.rectangle(wx + starXs[i]!, wTop + starYs[i]!, 1, 1, PALETTE.hudText, 0.9).setOrigin(0, 0).setDepth(0);
    }
    // mullion cross
    this.add.rectangle(wx, wTop, 2, wH, PALETTE.shopFloorEdge, 1).setOrigin(0.5, 0).setDepth(0);
    this.add.rectangle(wx, wTop + wH / 2, wW, 2, PALETTE.shopFloorEdge, 1).setOrigin(0.5, 0.5).setDepth(0);

    // Wall torches in the clear side margins (outside the centered card stack).
    this.buildTorch(42, 70);
    this.buildTorch(NATIVE_WIDTH - 42, 70);
  }

  private buildTorch(x: number, sconceY: number): void {
    this.add.rectangle(x, sconceY, 8, 2, PALETTE.shopFloorEdge, 1).setOrigin(0.5, 0).setDepth(0);
    this.add.rectangle(x, sconceY, 3, 7, PALETTE.shopFloorEdge, 1).setOrigin(0.5, 0).setDepth(0);
    const glow = this.add.circle(x, sconceY - 4, 13, PALETTE.shopLanternGlow, 0.1).setDepth(0);
    const outer = this.add.rectangle(0, 0, 6, 10, PALETTE.shopLantern, 1).setOrigin(0.5, 1);
    const inner = this.add.rectangle(0, -1, 3, 6, PALETTE.shopLanternGlow, 1).setOrigin(0.5, 1);
    const flame = this.add.container(x, sconceY, [outer, inner]).setDepth(0);
    this.tweens.add({
      targets: flame,
      scaleY: { from: 0.82, to: 1.14 },
      alpha: { from: 0.82, to: 1 },
      duration: 150 + Math.random() * 90,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.06, to: 0.18 },
      duration: 210,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  /**
   * A small floor signpost on the Tausch right side hinting that the World
   * Globe room lies that way — a post + an angled board with a globe + arrow.
   */
  private buildGlobeSignpost(): void {
    const sx = NATIVE_WIDTH - 52;
    this.add.rectangle(sx, FLOOR_Y, 3, 26, PALETTE.shopCardEdge, 1).setOrigin(0.5, 1).setDepth(1);
    this.add
      .rectangle(sx + 2, FLOOR_Y - 18, 30, 13, PALETTE.parchment, 1)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.shopCardEdge)
      .setDepth(1);
    this.add
      .text(sx + 17, FLOOR_Y - 18, "◎ ▶", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.hex(UGC_CYAN),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(1);
  }

  // ---- update loop --------------------------------------------------------

  override update(_t: number, dtMs: number): void {
    const input = this.input2.read();
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;

    // Tab toggle (Q/E + L1/R1).
    const intent = tabIntent(input.tabLeftPressed, input.tabRightPressed, this.activeTab);
    if (intent) {
      this.requestTab(intent);
      return;
    }

    // Esc / Start → leave the market from anywhere.
    if (input.pausePressed) {
      this.exitMarket();
      return;
    }

    // Select (Y / J) + sword swing.
    if (input.interactPressed || input.attackPressed) this.handleSelect();

    // Down scrolls the buffer (when off a platform); standing on a platform,
    // Down is the drop-through input (handled in handleMovement). Up no longer
    // scrolls — it hops the hero up instead (below).
    const onPlatform = this.selectedIndex() >= 0;
    if (!(input.moveY > 0.5 && onPlatform)) this.maybeScrollBuffer(input);
    else this.prevScrollDir = 0;

    // Up / W hops the hero up to the platform above (select the item one up),
    // exactly like the jump button. Edge-triggered so a held key hops once.
    const upHeld = input.moveY < -0.5;
    const jumpRequested = input.jumpPressed || (upHeld && !this.prevUpHeld);
    this.prevUpHeld = upHeld;

    this.handleMovement(input, body, jumpRequested);

    // Animations — never interrupt an in-flight swing.
    if (!this.isSwinging) {
      const moving = Math.abs(body?.velocity.x ?? 0) > 5;
      const want = moving ? KNIGHT_RUN_ANIM : KNIGHT_IDLE_ANIM;
      if (this.hero.anims.currentAnim?.key !== want) this.hero.play(want);
    }

    this.refreshSelectionHighlight();
    this.tickCards();
    this.updateExtras(input, dtMs);
    this.checkDoorTriggers(body);
  }

  private handleMovement(
    input: ReturnType<InputService["read"]>,
    body: Phaser.Physics.Arcade.Body | null,
    jumpRequested: boolean,
  ): void {
    const speed = GAMEPLAY.shopWalkVelocity;
    if (!body) return;
    body.setVelocityX(input.moveX * speed);
    if (input.moveX < -0.1) this.hero.setFlipX(true);
    else if (input.moveX > 0.1) this.hero.setFlipX(false);

    if (jumpRequested && body.blocked.down) {
      // Jumping from the topmost visible platform with hidden rows above
      // scrolls the buffer up one with the same slide the Up arrow uses.
      const sel = this.selectedIndex();
      if (sel === this.scrollOffset && this.scrollOffset > 0 && !this.animatingScroll) {
        this.slideScrollAnimated(-1, this.scrollOffset - 1);
        this.onScrolled("up", "jump");
      }
      body.setVelocityY(-GAMEPLAY.jumpVelocity);
      this.onJumped();
    }

    // Drop-through / scroll-down on the BOTTOM visible platform.
    const nowMs = this.time.now;
    if (input.moveY > 0.5 && body.blocked.down && nowMs - this.dropThroughArmedAtMs > 260) {
      const sel = this.selectedIndex();
      const lastSlotIdx = this.scrollOffset + this.visibleRows() - 1;
      const maxOffset = Math.max(0, this.itemCount() - this.visibleRows());
      const onBottomVisible = sel === lastSlotIdx && sel >= 0;
      const canScrollDown = this.scrollOffset < maxOffset;
      if (onBottomVisible && canScrollDown && !this.animatingScroll) {
        this.slideScrollAnimated(1, this.scrollOffset + 1);
        this.onScrolled("down", "drop");
        this.dropThroughArmedAtMs = nowMs;
      } else {
        this.dropThroughUntilMs = nowMs + 220;
        this.dropThroughArmedAtMs = nowMs;
      }
    }
  }

  private handleSelect(): void {
    this.playSwing();
    this.onSelectConfirm(this.selectedIndex());
  }

  private playSwing(): void {
    const now = this.time.now;
    if (now - this.lastSwingAtMs < GAMEPLAY.attackCooldownMs) return;
    this.lastSwingAtMs = now;
    if (!this.anims.exists(KNIGHT_ATTACK_ANIM)) return;
    this.isSwinging = true;
    this.hero.play(KNIGHT_ATTACK_ANIM, true);
    this.hero.once(Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + KNIGHT_ATTACK_ANIM, () => {
      this.isSwinging = false;
    });
  }

  // ---- scrolling ----------------------------------------------------------

  private maybeScrollBuffer(input: { moveY: number }): void {
    if (this.itemCount() === 0) return;
    // Only Down scrolls the buffer; Up hops the hero up instead (see update()).
    const dir = input.moveY > 0.5 ? 1 : 0;
    if (dir === this.prevScrollDir) return;
    this.prevScrollDir = dir;
    if (dir === 0) return;
    if (this.animatingScroll) return;
    const next = clampScrollOffset(this.scrollOffset + dir, this.itemCount(), this.visibleRows());
    if (next === this.scrollOffset) return;
    this.slideScrollAnimated(dir as 1 | -1, next);
    this.onScrolled(dir === 1 ? "down" : "up", "arrow");
  }

  protected slideScrollAnimated(dir: 1 | -1, next: number): void {
    const visibleCount = this.visibleRows();
    const enteringIdx = dir === 1 ? next + visibleCount - 1 : next;
    if (enteringIdx < 0 || enteringIdx >= this.itemCount()) {
      // Nothing to slide into — clamp and rebuild.
      this.scrollOffset = next;
      this.renderCards();
      return;
    }

    const yDelta = -dir * (rowTopY(1) - rowTopY(0));
    const enteringSlot = dir === 1 ? visibleCount - 1 : 0;
    const enteringStartY = rowTopY(enteringSlot) - yDelta;
    const newCard = this.createCard(enteringIdx, CARD_STACK_X, enteringStartY);
    newCard.container.setDepth(DEPTH_CARDS);

    const exitingSlotIdx = dir === 1 ? 0 : visibleCount - 1;
    const exitingCard = this.cards[exitingSlotIdx];

    // Commit the offset immediately so HUD widgets reading scrollOffset update
    // without waiting for the tween.
    this.scrollOffset = next;
    this.scrollIndicator.update(this.scrollOffset, this.itemCount(), this.visibleRows());

    this.animatingScroll = true;
    const targets = [...this.cards.map((c) => c.container), newCard.container];
    this.tweens.add({
      targets,
      y: `+=${yDelta}`,
      duration: 200,
      ease: "Cubic.easeOut",
      onComplete: () => {
        exitingCard?.destroy();
        const nextCards: BoardCard[] = [];
        if (dir === 1) {
          for (let i = 1; i < this.cards.length; i++) {
            if (this.cards[i] !== exitingCard) nextCards.push(this.cards[i]!);
          }
          nextCards.push(newCard);
        } else {
          nextCards.push(newCard);
          for (let i = 0; i < this.cards.length - 1; i++) {
            if (this.cards[i] !== exitingCard) nextCards.push(this.cards[i]!);
          }
        }
        this.cards = nextCards;
        this.animatingScroll = false;
        this.refreshSelectionHighlight();
        this.tickCards();
      },
    });
  }

  protected sweepToOffset(targetOffset: number): void {
    if (targetOffset === this.scrollOffset) return;
    if (this.animatingScroll) {
      this.scrollOffset = targetOffset;
      this.renderCards();
      return;
    }
    this.animatingScroll = true;
    const span = (rowTopY(1) - rowTopY(0)) * this.visibleRows();

    const exitingCards = this.cards;
    if (exitingCards.length > 0) {
      this.tweens.add({
        targets: exitingCards.map((c) => c.container),
        y: `+=${span}`,
        alpha: 0,
        duration: 260,
        ease: "Cubic.easeIn",
        onComplete: () => {
          for (const c of exitingCards) c.destroy();
        },
      });
    }

    this.scrollOffset = targetOffset;
    this.cards = [];
    for (let i = 0; i < this.visibleRows(); i++) {
      const itemIdx = targetOffset + i;
      if (itemIdx >= this.itemCount()) continue;
      const card = this.createCard(itemIdx, CARD_STACK_X, rowTopY(i) - span);
      card.container.setDepth(DEPTH_CARDS);
      this.cards.push(card);
    }
    this.scrollIndicator.update(this.scrollOffset, this.itemCount(), this.visibleRows());

    if (this.cards.length === 0) {
      this.animatingScroll = false;
      return;
    }
    this.tweens.add({
      targets: this.cards.map((c) => c.container),
      y: `+=${span}`,
      duration: 300,
      ease: "Cubic.easeOut",
      onComplete: () => {
        this.animatingScroll = false;
        this.refreshSelectionHighlight();
        this.tickCards();
        this.cards[0]?.animateArrive?.();
      },
    });
  }

  protected renderCards(): void {
    for (const c of this.cards) c.destroy();
    this.cards = [];
    this.scrollOffset = clampScrollOffset(this.scrollOffset, this.itemCount(), this.visibleRows());
    this.beforeRenderCards();
    for (let i = 0; i < this.visibleRows(); i++) {
      const itemIdx = this.scrollOffset + i;
      if (itemIdx >= this.itemCount()) continue;
      const card = this.createCard(itemIdx, CARD_STACK_X, rowTopY(i));
      card.container.setDepth(DEPTH_CARDS);
      this.cards.push(card);
    }
    this.refreshSelectionHighlight();
    this.scrollIndicator.update(this.scrollOffset, this.itemCount(), this.visibleRows());
  }

  // ---- selection ----------------------------------------------------------

  /** Selected buffer index = scrollOffset + standing row, or -1. */
  protected selectedIndex(): number {
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (!body || !body.blocked.down) return -1;
    const row = selectedRow(body.y + body.height, this.hero.x, this.visibleRows());
    return row < 0 ? -1 : this.scrollOffset + row;
  }

  protected refreshSelectionHighlight(): void {
    const sel = this.selectedIndex();
    for (let i = 0; i < this.cards.length; i++) this.cards[i]?.setSelected(this.scrollOffset + i === sel);
    for (let i = 0; i < this.platforms.length; i++) {
      this.platforms[i]!.fillColor = this.scrollOffset + i === sel ? UGC_CYAN : PALETTE.shopFloorEdge;
    }
    this.onSelectionChanged(sel);
  }

  private tickCards(): void {
    const now = this.time.now;
    for (const c of this.cards) c.tick?.(now);
  }

  // ---- doors --------------------------------------------------------------

  private checkDoorTriggers(body: Phaser.Physics.Arcade.Body | null): void {
    this.applyDoorGlow();
    if (this.time.now - this.enteredAtMs < ENTRY_DEBOUNCE_MS) return;
    const x = this.hero.x;
    if (atLeftDoor(x, 28) && (body?.blocked.left === true || atLeftDoor(x))) {
      this.fireDoor(this.doors.left);
    } else if (
      this.doors.right &&
      atRightDoor(x, NATIVE_WIDTH, 28) &&
      (body?.blocked.right === true || atRightDoor(x, NATIVE_WIDTH))
    ) {
      this.fireDoor(this.doors.right);
    }
  }

  private fireDoor(cfg: DoorConfig): void {
    switch (cfg.action) {
      case "exitMarket":
        this.exitMarket();
        return;
      case "toGallery":
        this.switchVia = "walk";
        this.switchTab("gallery", "fromLeft");
        return;
      case "toTrades":
        this.switchVia = "walk";
        this.switchTab("trades", "fromRight");
        return;
    }
  }

  private applyDoorGlow(): void {
    const x = this.hero.x;
    if (this.leftDoorGlow) this.leftDoorGlow.setAlpha(atLeftDoor(x, 28) ? 0.9 : 0.4);
    if (this.rightDoorGlow) this.rightDoorGlow.setAlpha(atRightDoor(x, NATIVE_WIDTH, 28) ? 0.9 : 0.4);
  }

  protected hex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}
